import { useState, useEffect } from 'preact/hooks'
import { touchPointsApi } from '@/api/touchPoints'
import type { TouchPointViewDto, TouchPointCreateDto, TouchPointUpdateDto } from '@/types/touchPoint'
import { useToast } from './useToast'

interface UseTouchPointManagerOptions {
  workspaceId: string
  campaignId?: string
  webId?: string
  typeFilters?: string[]
  isCampaignLevel?: boolean
}

export function useTouchPointManager({
  workspaceId,
  campaignId,
  webId,
  typeFilters,
  isCampaignLevel = false,
}: UseTouchPointManagerOptions) {
  const [touchPoints, setTouchPoints] = useState<TouchPointViewDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const { showSuccess, showError, showErrorUnless422 } = useToast()

  // Core loader: refreshes touchPoints/error. The loading spinner is the caller's call. clearOnError
  // blanks the list on failure (a fresh or param-change load must not show stale data); the background
  // reconcile passes false so a transient blip never wipes the meetings already on screen.
  const loadTouchPoints = async (clearOnError: boolean) => {
    setError(null)

    try {
      const response = isCampaignLevel && campaignId
        ? await touchPointsApi.getTouchPointsByCampaign(workspaceId, campaignId, typeFilters)
        : webId
        ? await touchPointsApi.getTouchPointsByWeb(workspaceId, webId, typeFilters)
        : { error: 'Invalid configuration', data: null }

      if (response.error) {
        setError(response.error)
        if (clearOnError) setTouchPoints([])
      } else {
        setTouchPoints(response.data || [])
      }
    } catch (err) {
      setError('Failed to load touchpoints')
      if (clearOnError) setTouchPoints([])
    }
  }

  // Fetch with the loading spinner (initial load + manual retry). Clears the list on error.
  const fetchTouchPoints = async () => {
    setLoading(true)
    try {
      await loadTouchPoints(true)
    } finally {
      setLoading(false)
    }
  }

  // Background reconcile without the spinner. Used after a live SignalR status change so the list does
  // not flash while pulling the recording/transcript URLs. Keeps the current list on a transient fetch
  // error so a background refetch can never blank what the user is looking at.
  const refetchQuietly = () => loadTouchPoints(false)

  // Auto-fetch on mount and when dependencies change
  useEffect(() => {
    fetchTouchPoints()
  }, [workspaceId, campaignId, webId, typeFilters?.join(',')])

  // Create touchpoint
  const createTouchPoint = async (dto: TouchPointCreateDto, successMessage = 'Touch Point Created', file?: File) => {
    setSaving(true)
    try {
      const response = await touchPointsApi.createTouchPoint(workspaceId, dto, file)

      if (response.error) {
        showErrorUnless422('Creation Failed', response)
        return { success: false, error: response.error }
      }

      // Add to local state
      setTouchPoints(prev => [...prev, response.data!])
      showSuccess(successMessage, 'The touch point was created successfully.')
      return { success: true, data: response.data }
    } catch (err) {
      showError('Error', 'An unexpected error occurred.')
      return { success: false, error: 'Unexpected error' }
    } finally {
      setSaving(false)
    }
  }

  // Update touchpoint
  const updateTouchPoint = async (
    touchPointId: string,
    dto: TouchPointUpdateDto,
    successMessage = 'Touch Point Updated',
    file?: File
  ) => {
    setSaving(true)
    try {
      const response = await touchPointsApi.updateTouchPoint(workspaceId, touchPointId, dto, file)

      if (response.error) {
        showErrorUnless422('Update Failed', response)
        return { success: false, error: response.error }
      }

      // Update local state
      setTouchPoints(prev => prev.map(tp => (tp.Id === touchPointId ? response.data! : tp)))
      showSuccess(successMessage, 'The touch point was updated successfully.')
      return { success: true, data: response.data }
    } catch (err) {
      showError('Error', 'An unexpected error occurred.')
      return { success: false, error: 'Unexpected error' }
    } finally {
      setSaving(false)
    }
  }

  // Delete touchpoint
  const deleteTouchPoint = async (touchPointId: string, touchPointName: string, successMessage = 'Touch Point Deleted') => {
    setDeleting(true)
    try {
      const response = await touchPointsApi.deleteTouchPoint(workspaceId, touchPointId)

      if (response.error) {
        showErrorUnless422('Deletion Failed', response)
        return { success: false, error: response.error }
      }

      // Remove from local state
      setTouchPoints(prev => prev.filter(tp => tp.Id !== touchPointId))
      showSuccess(successMessage, `"${touchPointName}" was deleted successfully.`)
      return { success: true }
    } catch (err) {
      showError('Error', 'An unexpected error occurred.')
      return { success: false, error: 'Unexpected error' }
    } finally {
      setDeleting(false)
    }
  }

  // Merge a live Webster bot-status change (SignalR) into the matching meeting. The event carries
  // status + hasRecording but not the transcript URL, so callers refetch on completion to fill it in.
  const applyBotStatusUpdate = (update: {
    touchPointId: string
    botStatus: string
    botStatusSubCode?: string | null
    hasRecording?: boolean
  }) => {
    setTouchPoints(prev => prev.map(tp => {
      if (tp.Id !== update.touchPointId || !tp.MeetingProperties) return tp
      return {
        ...tp,
        MeetingProperties: {
          ...tp.MeetingProperties,
          BotStatus: update.botStatus,
          BotStatusSubCode: update.botStatusSubCode ?? null,
          HasRecording: update.hasRecording ?? tp.MeetingProperties.HasRecording,
        },
      }
    }))
  }

  return {
    touchPoints,
    loading,
    error,
    saving,
    deleting,
    fetchTouchPoints,
    refetchQuietly,
    applyBotStatusUpdate,
    createTouchPoint,
    updateTouchPoint,
    deleteTouchPoint,
  }
}
